import React from 'react';
import { Grid, Box, Typography, Button, useMediaQuery, CircularProgress } from '@mui/material';
import { ReferralData } from '../types';
import { LEAD_STATUS, STATUS } from '../../LeadManagement/constants';
import InfoField from './InfoField';
import { getNestedProperty } from '../utils';
import { storeCDPProfileData } from '../utils/cdpProfileNavigation';

interface PatientInfoGridProps {
  row: ReferralData;
  onSendToCC?: () => void;
  isSending?: boolean;
  selectedDedupeRecord?: string;
  isProcessedTab?: boolean;
  fieldConfig?: Array<{ key: string; label: string }>;
  personalInfoHeaderText?: string;
  statusValue?: string;
  viewDetails?: { href: string; text: string };
  sendToCC?: string;
}

const PatientInfoGrid: React.FC<PatientInfoGridProps> = ({
  row,
  onSendToCC,
  isSending = false,
  selectedDedupeRecord,
  isProcessedTab = false,
  fieldConfig,
  personalInfoHeaderText = 'Personal Information',
  statusValue,
  viewDetails,
  sendToCC,
}) => {
  // Custom breakpoint: 4 columns only when screen width > 1200px
  const isLargeScreen = useMediaQuery('(min-width:1200px)');

  // Determine if Send To CC button should be disabled based on statusValue
  // NEW - always enable
  // MANUAL_REVIEW_REQUIRED - always disabled
  // AUTO_MERGE - enabled only when selectedDedupeRecord has value
  const currentStatus = statusValue || row.status;

  const isSendToCCDisabled =
    currentStatus === STATUS.MANUAL_REVIEW_REQUIRED ||
    (currentStatus === STATUS.AUTO_MERGE && !selectedDedupeRecord);

  const cdpProfileId = row.patientId;

  const handleViewCDPProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    storeCDPProfileData({
      ...row,
      ccPatientId: row.patientId,
      status: currentStatus,
      leadStatus: isProcessedTab ? LEAD_STATUS.PROCESSED : LEAD_STATUS.REQUESTED,
    });

    const url = `${viewDetails?.href}?id=${encodeURIComponent(row.patientId)}`;

    window.open(url, '_blank');
  };

  // Default field configuration if not provided
  const defaultPersonalFields = [
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zipcode', label: 'Zipcode' },
    { key: 'qualifiedProtocols', label: 'Qualified Protocols' },
  ];

  const personalFields =
    fieldConfig && fieldConfig.length > 0 ? fieldConfig : defaultPersonalFields;

  return (
    <Box>
      {/* Personal Information Section with Actions */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={1.25}>
          {/* Personal Details Header with Buttons */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontSize: '13px',
                  color: (theme: any) => theme.palette.primary?.dark || '#1976d2',
                }}
              >
                {personalInfoHeaderText}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {cdpProfileId && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleViewCDPProfile}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      borderColor: '#255E5E',
                      color: '#255E5E',
                      '&:hover': {
                        borderColor: '#1a4545',
                        backgroundColor: 'rgba(37, 94, 94, 0.04)',
                      },
                    }}
                  >
                    {viewDetails?.text || 'View Full CDP Profile'}
                  </Button>
                )}
                {onSendToCC && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onSendToCC}
                    disabled={isSendToCCDisabled || isSending}
                    startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      backgroundColor: '#255E5E',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#1a4545',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#ccc',
                        color: '#888',
                      },
                    }}
                  >
                    {isSending ? 'Sending...' : sendToCC || 'Send to CC'}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Patient Information Fields */}
          {personalFields.map((field) => {
            let fieldValue = getNestedProperty(row, field.key);
            // Capitalize first letter only for patientType field
            if (field.key === 'patientType' && fieldValue) {
              fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
            }
            return (
              <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={field.key}>
                <InfoField label={field.label} value={fieldValue} />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default PatientInfoGrid;
